<template>
  <div class="auth-page">
    <div class="auth-container">

      <!-- Brand -->
      <div class="auth-brand">
        <BrandLogo />
        <span class="auth-brand-subtitle">touchless AP</span>
      </div>

      <!-- Processing -->
      <div v-if="processing" class="auth-card auth-card--center">
        <div class="auth-spinner" aria-hidden="true"></div>
        <h2 class="auth-loading-title">Iniciando sesión…</h2>
        <p class="auth-loading-text">Configurando tu cuenta de forma segura.</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="auth-card auth-card--center">
        <div class="auth-warn-icon" aria-hidden="true">
          <v-icon size="36" color="#202124">mdi-alert-circle-outline</v-icon>
        </div>
        <h2 class="auth-loading-title">Error de autenticación</h2>
        <p class="auth-loading-text">{{ error }}</p>
        <button type="button" class="auth-btn-primary" @click="$router.push('/signin')">
          Volver al inicio de sesión
        </button>
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { supabase } from '@/services/supabase';
import { setAccessToken } from '@/services/funnelApi';
import { createHandoffCode, buildHandoffUrl } from '@/services/api';
import { okeyTrack, okeyTrackOnce } from '@/utils/track';
import BrandLogo from '@/components/brand/BrandLogo.vue';

export default Vue.extend({
  name: 'CallbackView',
  components: { BrandLogo },
  data() {
    return {
      processing: true,
      error: null as string | null,
    };
  },
  async mounted() {
    await this.handleCallback();
  },
  methods: {
    async handleCallback() {
      try {
        // OAuth providers (Google) return tokens in the URL fragment (#...).
        // Supabase's detectSessionInUrl reads them on getSession(); give it a
        // tick so the SDK can finish parsing.
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;

        if (!session) {
          // Check both query string and fragment for OAuth-style errors.
          const rawQuery = this.$route.query.error_description;
          const queryError = typeof rawQuery === 'string' ? rawQuery : '';
          const hashError = this.readHashError();
          throw new Error(queryError || hashError || 'No se pudo obtener la sesión');
        }

        const accessToken = session.access_token;
        const refreshToken = session.refresh_token;
        if (!accessToken || !refreshToken) {
          throw new Error('Tokens no disponibles');
        }

        // sign_up en la vía OAuth: aquí la autenticación ya ha terminado bien
        // (hay sesión y tokens). Para separar alta nueva de login recurrente se
        // usa la antigüedad de la cuenta: Supabase no expone un flag "is_new" en
        // el callback. Es una heurística, no un dato exacto — una cuenta creada
        // hace más de 5 min que vuelve a entrar cuenta como login, que es el
        // comportamiento deseado. Sin PII.
        //
        // Una sola emisión por cuenta: esta vista se ejecuta en CADA carga de
        // /callback y, con persistSession:false + detectSessionInUrl:true, cada
        // carga vuelve a ver la cuenta como recién creada mientras siga dentro
        // de la ventana de 5 minutos. Sin guarda, un alta emitía varios sign_up
        // (medido en producción: 4 para una sola cuenta).
        //
        // El user_id solo se usa como clave local de deduplicación; no viaja
        // en el evento.
        try {
          const createdAt = session.user?.created_at ? Date.parse(session.user.created_at) : NaN;
          const isNewAccount = Number.isFinite(createdAt) && Date.now() - createdAt < 5 * 60 * 1000;
          const userKey = session.user?.id ?? 'unknown';
          if (isNewAccount) {
            okeyTrackOnce(userKey, 'sign_up', { method: 'google' });
          } else {
            okeyTrack('login', { method: 'google' });
          }
        } catch { /* la analítica nunca puede romper el login */ }

        // El refresh token no se guarda en sessionStorage: el frontend nunca necesita
        // leerlo, y dejarlo ahí lo expone a cualquier script de la página.
        try { setAccessToken(accessToken); } catch { /* ignore */ }

        // Already-qualified users skip the questionnaire and go straight to the
        // app; only new users go through /qualify. Without this check, every
        // login bounced returning users back into the stepper.
        let qualified = false;
        try {
          const apiUrl = process.env.VUE_APP_API_URL || '';
          const resp = await fetch(`${apiUrl}/api/auth/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          if (resp.ok) {
            const profile = await resp.json();
            qualified = !!profile.qualified_at;
          }
        } catch { /* network error → treat as new user → qualify */ }

        // Ruta heredada: Google ya no aterriza aquí — la app arranca su propio
        // flujo PKCE en /auth/google y Supabase le devuelve el ?code=
        // directamente. Esto solo cubre enlaces antiguos, y en ese caso el
        // handoff va por código de un solo uso, nunca con tokens en la URL.
        const mainAppUrl = process.env.VUE_APP_MAIN_APP_URL || 'http://localhost:8080';
        const handoffCode = await createHandoffCode(accessToken, refreshToken);
        if (!handoffCode) {
          throw new Error('No se pudo completar el inicio de sesión. Inténtalo de nuevo.');
        }
        window.location.href = buildHandoffUrl(
          mainAppUrl, handoffCode, qualified ? '/inbox' : '/onboarding'
        );
      } catch (err) {
        this.error = (err as Error)?.message || 'Error al procesar la autenticación';
        this.processing = false;
      }
    },
    readHashError(): string {
      try {
        const hash = window.location.hash || '';
        if (!hash) return '';
        const params = new URLSearchParams(hash.replace(/^#/, ''));
        return params.get('error_description') || params.get('error') || '';
      } catch {
        return '';
      }
    },
  },
});
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  background: var(--casa-fondo-app);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--casa-espacio-6) var(--casa-espacio-4);
  font-family: 'Google Sans', 'SF Pro Display', ui-sans-serif, system-ui, sans-serif;
}

.auth-container {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--casa-espacio-5);
}

.auth-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--casa-espacio-1);
}

.auth-brand-subtitle {
  font-size: var(--casa-letra-control);
  line-height: 1.2;
  color: var(--casa-texto-tenue);
}

.auth-card {
  width: 100%;
  background: var(--casa-fondo);
  border-radius: var(--casa-radio-tarjeta);
  padding: var(--casa-espacio-12) 36px;
  border: 1px solid var(--casa-borde-fuerte);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.auth-card--center {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--casa-espacio-4);
}

.auth-loading-title {
  font-size: var(--casa-letra-titulo);
  font-weight: 600;
  color: var(--casa-texto);
  margin: 0;
}

.auth-loading-text {
  font-size: var(--casa-letra-texto);
  color: var(--casa-texto-suave);
  margin: 0;
  line-height: 1.6;
}

.auth-spinner {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 3px solid rgba(26, 115, 232, 0.16);
  border-top-color: var(--ii-accent);
  animation: auth-spin 0.7s linear infinite;
}

@keyframes auth-spin {
  to { transform: rotate(360deg); }
}

.auth-warn-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--casa-fondo-app);
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-btn-primary {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 50px;
  background: var(--ii-accent);
  color: var(--casa-texto-inverso);
  border: none;
  border-radius: var(--casa-radio-control);
  font-size: var(--casa-letra-cuerpo);
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;
  margin-top: var(--casa-espacio-2);
}

.auth-btn-primary:hover { background: var(--casa-primario-hover); }
</style>
